import { useEffect, useMemo, useState } from 'react';
import { Plus } from 'lucide-react';
import type {
  RoutingBreakerRecord,
  RoutingPolicyMode,
  RoutingSkuTargetRecord,
  RoutingSmartSettingsPatch,
  RoutingTimeWindowRecord,
  TargetProbeLayer,
  TargetProbeStateRecord,
} from '@nadoutong/contracts';
import {
  usePutRoutingSmartSettings,
  useRoutingSmartOverview,
  useRoutingSmartSettings,
} from '../../api/useRoutingSmart.ts';
import { coerceErrorMessage, useToast } from '../../components/Toast.tsx';
import { useT } from '../../i18n/index.ts';
import { Button, Chip, Select, TBody, Table, Th, THead, Tr } from '../../ui/index.ts';
import { TenantRoutingPoolShareBar } from './TenantRoutingPoolShareBar.tsx';
import { useTargetProbeLegs } from '../../api/useTargetProbes.ts';
import { useGuaranteedServiceList } from '../../api/guaranteedService.ts';
import { LEG_SKU_UNKNOWN } from '../guaranteed/legModel.ts';
import { LEG_SKU_ALL_OFF, legSkuArming } from '../guaranteed/skuArming.ts';
import { indexLegsByTargetId } from './probeDot.ts';
import { TenantRoutingSkuPoolRow } from './TenantRoutingSkuPoolRow.tsx';
import { TenantRoutingSmartModeSwitch } from './TenantRoutingSmartModeSwitch.tsx';
import { TenantRoutingSmartOverview } from './TenantRoutingSmartOverview.tsx';
import { TenantRoutingSmartPoolTable } from './TenantRoutingSmartPoolTable.tsx';
import type { SmartTargetFieldsPatch } from './TenantRoutingSmartPoolTable.tsx';
import { TenantRoutingSmartSettingsBar } from './TenantRoutingSmartSettingsBar.tsx';
import { TenantRoutingTimeWindows } from './TenantRoutingTimeWindows.tsx';
import { firstAttemptShares, shareByTargetId } from './firstAttemptShare.ts';
import {
  POLICY_MODE_OPTIONS,
  breakerForTarget,
  probeStateForTarget,
  policyModeHintKey,
  policyModeLabelKey,
  sortSkuPoolTargets,
} from '../tenantRoutingShared.ts';
import type { SkuOption } from '../tenantRoutingShared.ts';

/**
 * Right column of the config sheet (#1493): the target pool of the ONE
 * selected SKU.
 *
 * Extracted from `SkuPoolSection`, which the sheet used to render once per
 * catalog SKU — so the six-column table header and the policy selector below
 * it were repeated as many times as the namespace had models (six times on
 * dev). Mounted once here, they appear once.
 *
 * The caller keys this by SKU id, so switching SKUs remounts rather than
 * reconciles: the policy draft below belongs to the SKU it was typed against
 * and must not survive a switch.
 */

/**
 * The pool table's width contract (#1494).
 *
 * `table-fixed` is the load-bearing half. Under the default auto layout a
 * column grows to its widest cell's intrinsic width and `truncate` does not
 * cap it, so the table's width was a function of how long this tenant's
 * `capability_id` / `upstream_model_name` / `account_id` happened to be —
 * "976px" was one namespace's measurement, not a bound. Fixed layout makes the
 * declared `w-[NNNpx]` header widths authoritative and lets the cells' own
 * `truncate` actually truncate, so longer data changes nothing about the
 * layout.
 *
 * The `min-w` floor is what the drawer has to fit (asserted in
 * `test/drawer-width.test.tsx`): it is the sum of the declared column widths
 * (152 + 116 + 96 + 160 + 196 = 720) plus 220px of breathing room for the
 * unsized target column. Below it the wrapper scrolls rather than crushing the
 * action buttons — a constant-width scroll, never a data-driven one.
 *
 * The weight column is the widest of the sized five because it carries the
 * per-target first-attempt share bar and its percentage (#1495), not just the
 * raw number.
 *
 * Both halves are written as literals because Tailwind's JIT only emits the
 * arbitrary values it can literally see in the source — a computed
 * `min-w-[${n}px]` would produce no CSS at all. `test/drawer-width.test.tsx`
 * re-derives the sum from the `w-[NNNpx]` header utilities below and checks it
 * against this floor, so the two cannot drift apart silently.
 */
const SKU_POOL_TABLE_CLASS = 'table-fixed min-w-[940px]';

interface Props {
  readonly sku: SkuOption;
  /** `undefined` while this SKU's pool query is still resolving. */
  readonly targets: readonly RoutingSkuTargetRecord[] | undefined;
  /** False while this SKU's pool read is pending or failed — see below. */
  readonly targetsLoaded: boolean;
  readonly savedPolicyMode: RoutingPolicyMode;
  /**
   * False only while the policy read is still in flight — NOT "the read
   * succeeded". A policy PUT is an unconditional single-value upsert that the
   * server resolves without reference to that GET, so a failed read is no
   * reason to withhold the control: the operator picks a mode and the write
   * lands. (Contrast the add-target button below, which is fail-closed
   * because its PUT is insert-or-update against rows this sheet would then be
   * unable to vouch for.) Gating on read SUCCESS instead would leave the
   * button permanently grey with no stated reason after one 5xx.
   */
  readonly policyLoaded: boolean;
  readonly breakers: readonly RoutingBreakerRecord[];
  readonly probes: readonly TargetProbeStateRecord[];
  readonly probeLayer: TargetProbeLayer;
  readonly deleting: boolean;
  readonly savingPolicy: boolean;
  readonly resettingBreaker: boolean;
  readonly onAdd: () => void;
  readonly onEdit: (target: RoutingSkuTargetRecord) => void;
  readonly onDelete: (target: RoutingSkuTargetRecord) => void;
  readonly onPolicySave: (policyMode: RoutingPolicyMode) => void;
  readonly onBreakerReset: (record: RoutingBreakerRecord) => void;
  readonly onBreakerTrip: (target: RoutingSkuTargetRecord) => void;
  readonly onSaveSmartFields: (target: RoutingSkuTargetRecord, patch: SmartTargetFieldsPatch) => void;
  readonly savingSmartFields: boolean;
  /**
   * Every routing time window of the enclosing config (0044). One read per
   * config, not per SKU: a window is a group whose overrides may span models,
   * so the panel filters rather than fetches.
   */
  readonly timeWindows: readonly RoutingTimeWindowRecord[];
  readonly timeWindowsLoaded: boolean;
  readonly onAddTimeWindow: () => void;
  readonly onEditTimeWindow: (window: RoutingTimeWindowRecord) => void;
}

export function TenantRoutingSkuPoolPanel({
  sku,
  targets,
  targetsLoaded,
  savedPolicyMode,
  policyLoaded,
  breakers,
  probes,
  probeLayer,
  deleting,
  savingPolicy,
  resettingBreaker,
  onAdd,
  onEdit,
  onDelete,
  onPolicySave,
  onBreakerReset,
  onBreakerTrip,
  onSaveSmartFields,
  savingSmartFields,
  timeWindows,
  timeWindowsLoaded,
  onAddTimeWindow,
  onEditTimeWindow,
}: Props): JSX.Element {
  const t = useT();
  const toast = useToast();
  const probeLegs = useTargetProbeLegs();
  const probeByTargetId = useMemo(
    () => indexLegsByTargetId(probeLegs.data?.legs ?? []),
    [probeLegs.data],
  );
  const guaranteed = useGuaranteedServiceList();
  // Unknown until the list loads (a live removal must not read grey meanwhile);
  // an SKU without a guaranteed-service row has every mechanism off.
  const arming = useMemo(() => {
    if (guaranteed.data === undefined) return LEG_SKU_UNKNOWN;
    const row = guaranteed.data.sku.find((entry) => entry.sku_id === sku.skuId);
    return row === undefined ? LEG_SKU_ALL_OFF : legSkuArming(row);
  }, [guaranteed.data, sku.skuId]);
  const smartSettings = useRoutingSmartSettings(sku.skuId);
  const putSmartSettings = usePutRoutingSmartSettings();
  const smartEnabled = smartSettings.data?.enabled === true;
  const smartOverview = useRoutingSmartOverview(sku.skuId, smartEnabled);
  const orderedTargets = useMemo(() => sortSkuPoolTargets(targets ?? []), [targets]);
  // Computed once for the whole pool rather than per row: a share is only
  // meaningful against the pool's total weight, so a row cannot derive its own.
  const shares = useMemo(() => firstAttemptShares(orderedTargets), [orderedTargets]);
  const sharesById = useMemo(() => shareByTargetId(shares), [shares]);
  const emptyPool = targets !== undefined && !targets.some((target) => target.enabled);
  const [policyDraft, setPolicyDraft] = useState<RoutingPolicyMode>(savedPolicyMode);
  // The panel can mount before this SKU's policy row lands (the sheet fans the
  // catalog out), so the draft has to follow the saved value in as well as be
  // seeded from it.
  useEffect(() => {
    setPolicyDraft(savedPolicyMode);
  }, [savedPolicyMode]);

  function saveSmartSettings(body: RoutingSmartSettingsPatch): void {
    putSmartSettings.mutate(
      { skuId: sku.skuId, body },
      {
        onSuccess: () => {
          toast.success(
            body.enabled !== undefined
              ? t('tenant_routing.smart.toast_mode')
              : t('tenant_routing.smart.toast_settings'),
          );
        },
        onError: (error) => {
          toast.error(coerceErrorMessage(error));
        },
      },
    );
  }

  return (
    <div
      className="flex max-h-[70vh] min-w-0 flex-1 flex-col overflow-y-auto rounded-lg border border-line-soft bg-well"
      data-testid={`tenant-routing-sku-pool-${sku.skuId}`}
    >
      <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-2.5">
        <div className="flex min-w-0 items-baseline gap-2">
          <TenantRoutingSmartModeSwitch
            skuId={sku.skuId}
            settings={smartSettings.data}
            saving={putSmartSettings.isPending}
            locked={smartSettings.isPending || smartSettings.isError}
            onChange={(enabled) => saveSmartSettings({ enabled })}
          />
          <span className="font-mono text-body text-paper">{sku.model}</span>
          <span className="truncate font-mono text-micro text-paper-low">{sku.skuName}</span>
          {smartEnabled ? (
            <Chip tone="signal" lamp={false} testId={`tenant-routing-smart-badge-${sku.skuId}`}>
              {t('tenant_routing.smart.mode.smart')}
            </Chip>
          ) : null}
          {emptyPool ? (
            <Chip tone="warn" lamp={false} testId={`tenant-routing-sku-empty-${sku.skuId}`}>
              {t('tenant_routing.sku_pools.empty_pool')}
            </Chip>
          ) : null}
        </div>
        {/*
          Unavailable while this SKU's pool is unverifiable. The dialog's only
          outcome is a PUT whose insert-or-update fate depends on rows the
          sheet cannot currently vouch for, so the form is not offered at all
          rather than offered, filled in, and then refused on submit.

          The reason hangs on the WRAPPER: `Button`'s base class carries
          `disabled:pointer-events-none`, so a disabled button receives no
          mouseover and its own `title` would never render. `inline-flex`
          rather than a bare span so the button keeps its row alignment.
        */}
        <span
          className="inline-flex"
          title={targetsLoaded ? undefined : t('tenant_routing.pool.write_blocked_targets_unknown')}
        >
          <Button
            size="sm"
            disabled={!targetsLoaded}
            onClick={onAdd}
            data-testid={`tenant-routing-sku-pool-add-${sku.skuId}`}
          >
            <Plus aria-hidden="true" className="h-3.5 w-3.5" />
            {t('tenant_routing.pool.add_target')}
          </Button>
        </span>
      </div>
      {smartSettings.isPending ? (
        <p
          className="m-0 px-4 pb-3 text-dense text-paper-low"
          data-testid={`tenant-routing-smart-settings-loading-${sku.skuId}`}
        >
          {t('tenant_routing.smart.settings_loading')}
        </p>
      ) : smartSettings.isError ? (
        <div
          role="alert"
          className="mx-4 mb-3 flex items-center justify-between gap-3 rounded-lg border border-bad-line bg-bad-dim px-3 py-2"
          data-testid={`tenant-routing-smart-settings-error-${sku.skuId}`}
        >
          <span className="text-dense text-bad-bright">{t('tenant_routing.smart.settings_error')}</span>
          <Button
            size="sm"
            onClick={() => void smartSettings.refetch()}
            data-testid={`tenant-routing-smart-settings-retry-${sku.skuId}`}
          >
            {t('common.retry')}
          </Button>
        </div>
      ) : orderedTargets.length === 0 ? (
        <p
          className="m-0 px-4 pb-3 text-dense text-paper-low"
          data-testid={`tenant-routing-sku-pool-no-targets-${sku.skuId}`}
        >
          {t('tenant_routing.sku_pools.empty')}
        </p>
      ) : smartEnabled ? (
        <TenantRoutingSmartPoolTable
          skuId={sku.skuId}
          targets={orderedTargets}
          overviewTargets={smartOverview.data?.targets ?? []}
          overviewLoaded={smartOverview.data !== undefined}
          breakers={breakers}
          deleting={deleting}
          resettingBreaker={resettingBreaker}
          savingSmartFields={savingSmartFields}
          onSaveSmartFields={onSaveSmartFields}
          onEdit={onEdit}
          onDelete={onDelete}
          onBreakerReset={onBreakerReset}
          onBreakerTrip={onBreakerTrip}
          probeByTargetId={probeByTargetId}
          arming={arming}
          probeLayer={probeLayer}
        />
      ) : (
        <>
          <TenantRoutingPoolShareBar targets={orderedTargets} shares={shares} skuId={sku.skuId} />
          <Table className="rounded-none border-0 bg-transparent" tableClassName={SKU_POOL_TABLE_CLASS}>
            <THead>
              <Tr interactive={false}>
                <Th className="w-[152px]">{t('tenant_routing.default_channels.upstream_channel')}</Th>
                {/* No width: the target column absorbs whatever is left over. */}
                <Th>{t('tenant_routing.table.target')}</Th>
                <Th num className="w-[116px]">{t('tenant_routing.default_channels.weight')}</Th>
                <Th num className="w-[96px]">{t('tenant_routing.pool.retry_order')}</Th>
                <Th className="w-[160px]">{t('tenant_routing.table.status')}</Th>
                <Th className="w-[196px] text-right">{t('tenant_routing.table.actions')}</Th>
              </Tr>
            </THead>
            <TBody>
              {orderedTargets.map((target) => (
                <TenantRoutingSkuPoolRow
                  key={target.id}
                  target={target}
                  breaker={breakerForTarget(breakers, target)}
                  probe={probeStateForTarget(probes, target, probeLayer)}
                  probeLegs={probeByTargetId.get(target.id) ?? null}
                  arming={arming}
                  probeLayer={probeLayer}
                  share={sharesById.get(target.id) ?? null}
                  deleting={deleting}
                  resettingBreaker={resettingBreaker}
                  onEdit={onEdit}
                  onDelete={onDelete}
                  onBreakerReset={onBreakerReset}
                  onBreakerTrip={onBreakerTrip}
                />
              ))}
            </TBody>
          </Table>
        </>
      )}
      {/*
        Below the pool, above the policy selector: the weights the table shows
        are the standard ones, and this band is what says when they are the
        ones actually in force.
      */}
      {smartSettings.isPending || smartSettings.isError ? null : (
      <TenantRoutingTimeWindows
        skuId={sku.skuId}
        targets={orderedTargets}
        windows={timeWindows}
        windowsLoaded={timeWindowsLoaded}
        onAdd={onAddTimeWindow}
        onEdit={onEditTimeWindow}
      />
      )}
      {smartEnabled && smartSettings.data !== undefined ? (
        <>
          <TenantRoutingSmartSettingsBar
            skuId={sku.skuId}
            settings={smartSettings.data}
            saving={putSmartSettings.isPending}
            onSave={(patch) => saveSmartSettings(patch)}
          />
          <TenantRoutingSmartOverview
            skuId={sku.skuId}
            skuName={sku.skuName}
            overview={smartOverview.data}
            loading={smartOverview.isPending}
            failed={smartOverview.isError}
            onRetry={() => void smartOverview.refetch()}
          />
        </>
      ) : smartSettings.isPending || smartSettings.isError ? null : (
        <div className="mt-auto flex flex-wrap items-center gap-2 border-t border-line-soft/50 px-4 py-2.5">
          <span className="text-label text-paper-low">{t('tenant_routing.default_channels.policy_mode')}</span>
          <Select
            className="min-w-52"
            value={policyDraft}
            onValueChange={(value) => {
              const mode = POLICY_MODE_OPTIONS.find((candidate) => candidate === value);
              if (mode !== undefined) {
                setPolicyDraft(mode);
              }
            }}
            options={POLICY_MODE_OPTIONS.map((mode) => ({ value: mode, label: t(policyModeLabelKey(mode)) }))}
            testId={`tenant-routing-sku-policy-mode-${sku.skuId}`}
          />
          <span className="min-w-0 flex-1 text-label text-paper-low">{t(policyModeHintKey(policyDraft))}</span>
          <Button
            size="sm"
            disabled={savingPolicy || !policyLoaded || policyDraft === savedPolicyMode}
            onClick={() => onPolicySave(policyDraft)}
            data-testid={`tenant-routing-sku-policy-save-${sku.skuId}`}
          >
            {t('tenant_routing.default_channels.save_policy')}
          </Button>
        </div>
      )}
    </div>
  );
}
